// ─── Realistic mock data for development / preview ────────────────────────────
// The frontend ships with this data active when VITE_USE_MOCK=true (default in dev)
// so you can see a fully-featured UI without a running backend.

import type { Thread, Label, User, Message } from './types'

export const MOCK_USER: User = {
  id: '00000000-0000-0000-0000-000000000001',
  email: 'alex@tmail.app',
  name: 'Alex Mercer',
  avatar_url: undefined,
  timezone: 'America/New_York',
  role: 'superadmin',
  signature: '<p>—<br/>Alex Mercer<br/><a href="mailto:alex@tmail.app">alex@tmail.app</a></p>',
  settings: { theme: 'dark', density: 'comfortable', send_shortcuts: true },
}

// ─── System labels (mirrors create_system_labels() PostgreSQL trigger) ────────

export const MOCK_LABELS: Label[] = [
  // Primary folders
  { id: 'l-inbox',      name: 'Inbox',      slug: 'inbox',      color: '#4F8EF7', is_system: true,  type: 'folder', unread_count: 24, total_count: 847 },
  { id: 'l-starred',    name: 'Starred',    slug: 'starred',    color: '#FBBF24', is_system: true,  type: 'label',  unread_count: 3,  total_count: 28 },
  { id: 'l-snoozed',    name: 'Snoozed',    slug: 'snoozed',    color: '#F59E0B', is_system: true,  type: 'folder', unread_count: 2,  total_count: 4 },
  { id: 'l-sent',       name: 'Sent',       slug: 'sent',       color: '#6DB87A', is_system: true,  type: 'folder', unread_count: 0,  total_count: 412 },
  { id: 'l-drafts',     name: 'Drafts',     slug: 'drafts',     color: '#F59E0B', is_system: true,  type: 'folder', unread_count: 2,  total_count: 5 },
  // Extended folders (shown under More)
  { id: 'l-important',  name: 'Important',  slug: 'important',  color: '#F59E0B', is_system: true,  type: 'label',  unread_count: 5,  total_count: 38 },
  { id: 'l-scheduled',  name: 'Scheduled',  slug: 'scheduled',  color: '#A78BFA', is_system: true,  type: 'folder', unread_count: 0,  total_count: 2 },
  { id: 'l-all_mail',   name: 'All Mail',   slug: 'all_mail',   color: '#6B7280', is_system: true,  type: 'folder', unread_count: 0,  total_count: 1502 },
  { id: 'l-spam',       name: 'Spam',       slug: 'spam',       color: '#E85454', is_system: true,  type: 'folder', unread_count: 7,  total_count: 63 },
  { id: 'l-trash',      name: 'Trash',      slug: 'trash',      color: '#9CA3AF', is_system: true,  type: 'folder', unread_count: 0,  total_count: 102 },
  // Category labels (Gmail-style inbox tabs)
  { id: 'l-social',     name: 'Social',     slug: 'social',     color: '#5B9FE8', is_system: true,  type: 'folder', unread_count: 0,  total_count: 89 },
  { id: 'l-updates',    name: 'Updates',    slug: 'updates',    color: '#F59E0B', is_system: true,  type: 'folder', unread_count: 12, total_count: 233 },
  { id: 'l-forums',     name: 'Forums',     slug: 'forums',     color: '#6DB87A', is_system: true,  type: 'folder', unread_count: 0,  total_count: 47 },
  { id: 'l-promotions', name: 'Promotions', slug: 'promotions', color: '#E07040', is_system: true,  type: 'folder', unread_count: 3,  total_count: 315 },
  // User labels
  { id: 'l-work',       name: 'Work',       slug: 'work',       color: '#8B5CF6', is_system: false, type: 'label',  unread_count: 5,  total_count: 156 },
  { id: 'l-finance',    name: 'Finance',    slug: 'finance',    color: '#10B981', is_system: false, type: 'label',  unread_count: 1,  total_count: 44 },
  { id: 'l-travel',     name: 'Travel',     slug: 'travel',     color: '#F97316', is_system: false, type: 'label',  unread_count: 0,  total_count: 19 },
]

// Convenient accessors
const L = Object.fromEntries(MOCK_LABELS.map((l) => [l.slug, l])) as Record<string, Label>

const now = new Date()
const minsAgo = (m: number) => new Date(now.getTime() - m * 60 * 1000).toISOString()
const hoursAgo = (h: number) => minsAgo(h * 60)
const daysAgo = (d: number) => hoursAgo(d * 24)

const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString()
const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString()

// ─── Mock messages for threads with attachments ───────────────────────────────

const MSG_T01: Message[] = [
  {
    id: 'm-t01-1', thread_id: 't-01',
    from_address: 'jordan@acme.com', from_name: 'Jordan Hayes',
    to_addresses: [{ name: 'Alex Mercer', address: 'alex@tmail.app' }],
    cc_addresses: [{ name: 'Sarah Kim', address: 'sarah@acme.com' }],
    bcc_addresses: [],
    subject: 'Q3 Infrastructure Review — Action Required',
    date: minsAgo(90),
    snippet: "Hi Alex, following up on yesterday's call. Please review the attached proposal.",
    body_html: '<p>Hi Alex,</p><p>Following up on yesterday\'s call. We need to finalize the budget allocation for the ScyllaDB migration before Friday.</p><p>Can you review the attached proposal and the budget spreadsheet?</p><p>Best,<br/>Jordan</p>',
    is_read: false, is_starred: false, is_important: true, is_draft: false, is_sent: false, is_spam: false, is_trashed: false,
    size_bytes: 18400,
    attachments: [
      { id: 'att-01a', filename: 'ScyllaDB_Migration_Proposal.docx', content_type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', size_bytes: 84320, is_inline: false },
      { id: 'att-01b', filename: 'Q3_Budget_Breakdown.xlsx', content_type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', size_bytes: 47800, is_inline: false },
      { id: 'att-01c', filename: 'architecture_diagram.png', content_type: 'image/png', size_bytes: 213400, is_inline: false },
    ],
  },
  {
    id: 'm-t01-2', thread_id: 't-01',
    from_address: 'sarah@acme.com', from_name: 'Sarah Kim',
    to_addresses: [{ name: 'Alex Mercer', address: 'alex@tmail.app' }, { name: 'Jordan Hayes', address: 'jordan@acme.com' }],
    cc_addresses: [],
    bcc_addresses: [],
    subject: 'Re: Q3 Infrastructure Review — Action Required',
    date: minsAgo(42),
    snippet: "I've reviewed the proposal. The timeline looks aggressive — can we discuss?",
    body_html: '<p>Jordan,</p><p>I\'ve reviewed the proposal. The timeline looks aggressive — can we schedule a quick sync to discuss the phased rollout options?</p><p>Sarah</p>',
    is_read: false, is_starred: false, is_important: false, is_draft: false, is_sent: false, is_spam: false, is_trashed: false,
    size_bytes: 4200,
    attachments: [],
  },
]

const MSG_T02: Message[] = [
  {
    id: 'm-t02-1', thread_id: 't-02',
    from_address: 'billing@stripe.com', from_name: 'Stripe Billing',
    to_addresses: [{ name: 'Alex Mercer', address: 'alex@tmail.app' }],
    cc_addresses: [], bcc_addresses: [],
    subject: 'Your invoice #INV-2024-1192 is ready',
    date: minsAgo(32),
    snippet: 'Your invoice for the month of November is available.',
    body_html: '<p>Hi Alex,</p><p>Your invoice <strong>#INV-2024-1192</strong> for November is ready.</p><p>Total amount due: <strong>$1,240.00</strong><br/>Payment due: Dec 15, 2024</p><p>Please find the PDF invoice attached.</p>',
    is_read: false, is_starred: false, is_important: false, is_draft: false, is_sent: false, is_spam: false, is_trashed: false,
    size_bytes: 9800,
    attachments: [
      { id: 'att-02a', filename: 'Invoice_INV-2024-1192.pdf', content_type: 'application/pdf', size_bytes: 128000, is_inline: false },
    ],
  },
]

const MSG_T04: Message[] = [
  {
    id: 'm-t04-1', thread_id: 't-04',
    from_address: 'no-reply@aa.com', from_name: 'American Airlines',
    to_addresses: [{ name: 'Alex Mercer', address: 'alex@tmail.app' }],
    cc_addresses: [], bcc_addresses: [],
    subject: 'Flight confirmation — NYC → SFO Nov 28',
    date: hoursAgo(5),
    snippet: 'Your booking is confirmed. Flight AA4872.',
    body_html: '<p>Dear Alex Mercer,</p><p>Your booking is confirmed.</p><ul><li>Flight: <strong>AA4872</strong></li><li>Route: JFK → SFO</li><li>Departure: Nov 28, 07:15 AM</li><li>Arrival: Nov 28, 10:40 AM</li><li>Seat: <strong>14A</strong> (Window)</li></ul><p>Your e-ticket and boarding pass are attached.</p>',
    is_read: true, is_starred: true, is_important: false, is_draft: false, is_sent: false, is_spam: false, is_trashed: false,
    size_bytes: 22400,
    attachments: [
      { id: 'att-04a', filename: 'AA4872_ETicket.pdf', content_type: 'application/pdf', size_bytes: 96000, is_inline: false },
      { id: 'att-04b', filename: 'boarding_pass_14A.png', content_type: 'image/png', size_bytes: 58000, is_inline: false },
    ],
  },
]

export const MOCK_THREADS: Thread[] = [
  // ── Inbox ──
  {
    id: 't-01',
    subject: 'Q3 Infrastructure Review — Action Required',
    snippet: "Hi Alex, following up on yesterday's call. We need to finalize the budget allocation for the ScyllaDB migration before Friday. Can you review the attached proposal?",
    message_count: 4, unread_count: 2, has_attachments: true, is_starred: true, is_important: true,
    participants: ['jordan@acme.com', 'sarah@acme.com', 'alex@tmail.app'],
    last_message_at: minsAgo(8),
    labels: [L.inbox, L.work],
    messages: MSG_T01,
  },
  {
    id: 't-02',
    subject: 'Your invoice #INV-2024-1192 is ready',
    snippet: 'Your invoice for the month of November is available in your billing portal. Total amount due: $1,240.00. Payment due: Dec 15, 2024.',
    message_count: 1, unread_count: 1, has_attachments: true, is_starred: false, is_important: false,
    participants: ['billing@stripe.com'],
    last_message_at: minsAgo(32),
    labels: [L.inbox, L.finance, L.updates],
    messages: MSG_T02,
  },
  {
    id: 't-03',
    subject: 'Re: Figma design system review',
    snippet: "Thanks for the detailed feedback! I've updated the color tokens and component library based on your notes. The main changes are in the typography scale and the spacing system.",
    message_count: 7, unread_count: 0, has_attachments: false, is_starred: false, is_important: false,
    participants: ['mia@studio.io', 'alex@tmail.app', 'carlos@studio.io'],
    last_message_at: hoursAgo(2),
    labels: [L.inbox],
  },
  {
    id: 't-04',
    subject: 'Flight confirmation — NYC → SFO Nov 28',
    snippet: 'Your booking is confirmed. Flight AA4872 departs JFK 07:15, arrives SFO 10:40. Seat 14A (window). Check-in opens 24 hours before departure.',
    message_count: 2, unread_count: 0, has_attachments: true, is_starred: true, is_important: false,
    participants: ['no-reply@aa.com'],
    last_message_at: hoursAgo(5),
    labels: [L.inbox, L.travel, L.updates],
    messages: MSG_T04,
  },
  {
    id: 't-05',
    subject: 'Go 1.23 release — new features you should know',
    snippet: 'The Go 1.23 release includes significant improvements to the range-over-func iterator pattern, enhanced profile-guided optimization, and a new unique package for interning values.',
    message_count: 1, unread_count: 1, has_attachments: false, is_starred: false, is_important: false,
    participants: ['newsletter@go.dev'],
    last_message_at: hoursAgo(9),
    labels: [L.inbox, L.updates],
  },
  {
    id: 't-06',
    subject: 'Security alert: new sign-in from Chrome/macOS',
    snippet: "We noticed a sign-in to your account from a new device. If this was you, no action is needed. If this wasn't you, please secure your account immediately.",
    message_count: 1, unread_count: 0, has_attachments: false, is_starred: false, is_important: true,
    participants: ['security@github.com'],
    last_message_at: hoursAgo(14),
    labels: [L.inbox, L.updates],
  },
  {
    id: 't-07',
    subject: 'Re: Proposal for new team structure',
    snippet: "Alex, I think your proposal makes sense for the current growth stage. My main concern is how we handle cross-functional projects — the matrix structure could create confusion about reporting lines.",
    message_count: 12, unread_count: 3, has_attachments: false, is_starred: false, is_important: false,
    participants: ['cto@tmail.app', 'hr@tmail.app', 'alex@tmail.app'],
    last_message_at: daysAgo(1),
    labels: [L.inbox, L.work],
  },
  {
    id: 't-08',
    subject: 'Meilisearch 1.9 — Federated search & new ranking rules',
    snippet: 'Meilisearch 1.9 ships federated search across multiple indexes, significantly improved multi-tenant filtering performance, and new ranking rules for hybrid vector + keyword search.',
    message_count: 1, unread_count: 0, has_attachments: false, is_starred: false, is_important: false,
    participants: ['news@meilisearch.com'],
    last_message_at: daysAgo(1),
    labels: [L.inbox, L.updates],
  },
  {
    id: 't-09',
    subject: 'Annual subscription renewal — 15% discount enclosed',
    snippet: "Your annual subscription to Linear renews in 30 days. As a loyal customer, we'd like to offer you a 15% discount on your next renewal. Use code RENEW15 at checkout.",
    message_count: 1, unread_count: 0, has_attachments: false, is_starred: false, is_important: false,
    participants: ['team@linear.app'],
    last_message_at: daysAgo(2),
    labels: [L.inbox, L.promotions],
  },
  {
    id: 't-10',
    subject: 'PR Review: feat/jmap-client-integration',
    snippet: 'Left a few comments on the JMAP session management — the token refresh logic should handle concurrent requests more gracefully. Otherwise LGTM. +1 to merge after those are addressed.',
    message_count: 5, unread_count: 0, has_attachments: false, is_starred: false, is_important: false,
    participants: ['priya@tmail.app', 'alex@tmail.app'],
    last_message_at: daysAgo(2),
    labels: [L.inbox, L.work],
  },
  {
    id: 't-11',
    subject: 'Team lunch this Friday — vote for location',
    snippet: "We're doing team lunch Friday at noon. I've put together a quick poll: Tartine Manufactory, Che Fico, or Nopa? Vote by EOD Thursday so I can make a reservation.",
    message_count: 8, unread_count: 0, has_attachments: false, is_starred: false, is_important: false,
    participants: ['ops@tmail.app', 'alex@tmail.app'],
    last_message_at: daysAgo(3),
    labels: [L.inbox],
  },
  {
    id: 't-12',
    subject: 'Stalwart Enterprise license — renewal quote',
    snippet: 'Dear Alex, please find attached your renewal quote for Stalwart Mail Server Enterprise (50 node cluster). Valid until Dec 31, 2024. Includes 24/7 support and priority patches.',
    message_count: 2, unread_count: 0, has_attachments: true, is_starred: true, is_important: false,
    participants: ['sales@stalw.art'],
    last_message_at: daysAgo(3),
    labels: [L.inbox, L.finance],
  },

  // ── Snoozed threads (hidden from inbox, visible in Snoozed folder) ──
  {
    id: 't-s1',
    subject: 'Reminder: Review Q4 OKRs before board meeting',
    snippet: 'This is a reminder you set. The board meeting is next Thursday — please review and update your OKR progress before then.',
    message_count: 1, unread_count: 0, has_attachments: false, is_starred: false, is_important: true,
    participants: ['alex@tmail.app'],
    last_message_at: daysAgo(5),
    labels: [L.work],
    snoozed_until: tomorrow,
  },
  {
    id: 't-s2',
    subject: 'Re: Open source sponsorship request',
    snippet: "Thanks for reaching out about sponsoring the project. I'll follow up after our next budget review meeting.",
    message_count: 3, unread_count: 0, has_attachments: false, is_starred: false, is_important: false,
    participants: ['oss@contributor.dev', 'alex@tmail.app'],
    last_message_at: daysAgo(4),
    labels: [],
    snoozed_until: nextWeek,
  },

  // ── Scheduled / outbox ──
  {
    id: 't-sc1',
    subject: 'Monthly infrastructure cost report — April 2026',
    snippet: 'Scheduled to send tomorrow at 9:00 AM. Attached is the monthly breakdown of infrastructure costs across all clusters.',
    message_count: 1, unread_count: 0, has_attachments: true, is_starred: false, is_important: false,
    participants: ['alex@tmail.app', 'finance@tmail.app'],
    last_message_at: minsAgo(15),
    labels: [L.scheduled],
    scheduled_send_at: tomorrow,
  },

  // ── Social ──
  {
    id: 't-social1',
    subject: 'Priya Sharma accepted your connection request',
    snippet: "You're now connected with Priya Sharma, Senior Engineer at Acme Corp. Start a conversation!",
    message_count: 1, unread_count: 0, has_attachments: false, is_starred: false, is_important: false,
    participants: ['notifications@linkedin.com'],
    last_message_at: hoursAgo(6),
    labels: [L.social],
  },

  // ── Promotions ──
  {
    id: 't-promo1',
    subject: '48-hour flash sale — up to 60% off developer tools',
    snippet: 'Exclusive deals on JetBrains, GitHub Copilot, Netlify Pro, and more. Offer ends Sunday at midnight.',
    message_count: 1, unread_count: 1, has_attachments: false, is_starred: false, is_important: false,
    participants: ['deals@devtools.io'],
    last_message_at: hoursAgo(3),
    labels: [L.promotions],
  },
  {
    id: 't-promo2',
    subject: 'Your Vercel plan is expiring — upgrade to keep your project live',
    snippet: 'Your free tier project "tmail-frontend" will be paused in 7 days. Upgrade to Pro to keep it running.',
    message_count: 1, unread_count: 1, has_attachments: false, is_starred: false, is_important: false,
    participants: ['noreply@vercel.com'],
    last_message_at: daysAgo(1),
    labels: [L.promotions],
  },
]

export const USE_MOCK = import.meta.env.VITE_USE_MOCK !== 'false'
